/**
 * postMessage Bridge プロトコル定義
 *
 * 子（embedded.js が iframe 内で動作）と親（parent-bridge.js）の通信仕様。
 * embedded.tsx と parent-bridge.tsx の両方から import する。
 *
 * バージョン skew（embedded と parent-bridge のデプロイ差）に耐性を持たせるため、
 * 全メッセージに version を付与し、不一致時は受信側が安全側に倒す。
 */

import type {
  PublicTenant,
  ScenarioWithNodesAndVideoUrls,
} from '../../orval/generated/public/model'

export const PROTOCOL_VERSION = '1.0' as const

export const MESSAGE_TYPES = {
  PING: 'moovy:bridge-ping',
  ACK: 'moovy:bridge-ack',
  OPEN: 'moovy:open',
  CLOSE: 'moovy:close',
  /**
   * 親→子の broadcast。parent-bridge.js が遅延ロードされた場合に
   * 既に inline-fallback に倒れた iframe にハンドシェイクを再試行させるため。
   */
  BRIDGE_HELLO: 'moovy:bridge-hello',
  /**
   * 案E: hidden pre-mount 用の事前通知。
   * iframe 側で SWR が解決した時点で scenario/tenant データと共に送信される。
   * 受信した親はユーザーがクリックする前に EmbeddingAiInterview を hidden で
   * mount しておき、OPEN 受信時は openHandle.open(videoId) を呼ぶだけで済むため、
   * クリック→モーダル可視化の経路が大幅に短縮される。
   */
  PREPARE: 'moovy:prepare',
} as const

type MessageType = (typeof MESSAGE_TYPES)[keyof typeof MESSAGE_TYPES]

type BaseMessage = {
  version: typeof PROTOCOL_VERSION
  instanceId: string
}

export type PingMessage = BaseMessage & { type: typeof MESSAGE_TYPES.PING }
export type AckMessage = BaseMessage & { type: typeof MESSAGE_TYPES.ACK }
export type OpenMessage = BaseMessage & {
  type: typeof MESSAGE_TYPES.OPEN
  payload: OpenPayload
}
export type CloseMessage = BaseMessage & { type: typeof MESSAGE_TYPES.CLOSE }
export type HelloMessage = BaseMessage & { type: typeof MESSAGE_TYPES.BRIDGE_HELLO }
export type PrepareMessage = BaseMessage & {
  type: typeof MESSAGE_TYPES.PREPARE
  payload: PreparePayload
}

/**
 * 案E: PREPARE メッセージで親に渡す事前データ。
 * scenarioId だけは必須（親が React tree の key として利用）。
 * scenario/tenant は SWR cache 共有（案C）と同じ目的で同梱する。
 */
export type PreparePayload = {
  scenarioId: string
  tenantId?: string
  scenario?: ScenarioWithNodesAndVideoUrls
  tenant?: PublicTenant | null
}

export type OpenPayload = {
  scenarioId: string
  /**
   * data-tenant-id 由来。指定があれば parent-bridge は scenarios/tenants を並列取得できる。
   * 省略時は parent-bridge 側で scenario レスポンスの tenantId にフォールバック。
   */
  tenantId?: string
  videoId?: string
  /**
   * 案C: SWR cache 共有
   * iframe 側で取得済みの scenario / tenant データをそのまま親に渡し、
   * 親側 EmbeddingAiInterview の useSWR が cache hit で即解決できるようにする。
   * これにより親で同じ API を再 fetch する RTT（推定 100〜200ms × 2 本）を 0 に近づける。
   *
   * 互換性: version=1.0 の embedded/parent-bridge では optional として扱う。
   * 旧 parent-bridge.js が新 embedded.js から受信した場合は無視される（無害）。
   * 新 parent-bridge.js が旧 embedded.js から受信した場合は undefined として扱い、
   * 従来どおり親側で再 fetch する（性能劣化なし、機能は健全）。
   */
  scenario?: ScenarioWithNodesAndVideoUrls
  tenant?: PublicTenant | null
}

export type MoovyMessage =
  | PingMessage
  | AckMessage
  | OpenMessage
  | CloseMessage
  | HelloMessage
  | PrepareMessage

/**
 * postMessage の origin 許可リスト（完全一致）
 *
 * - 'null': sandbox iframe（特に srcdoc ベース）の origin
 * - 'https://studio.moovy.jp': 本番配信ドメイン
 */
export const ALLOWED_ORIGINS: ReadonlyArray<string> = ['null', 'https://studio.moovy.jp']

/**
 * 動的サブドメインを持つ許可オリジンのパターン
 *
 * STUDIO（studio.design）は Embed Box 内のスクリプトを
 * <unique-id>.studioiframesandbox.com というユニークサブドメインの iframe に
 * ホストするため、サブドメインのワイルドカードを許可する必要がある。
 *
 * セキュリティ上の注意点：
 * - ^ と $ で完全一致を強制（プレフィックス/サフィックス偽装攻撃を防ぐ）
 * - https のみ許可（http だと中間者攻撃の余地あり）
 * - サブドメイン部分は [a-z0-9-]+ に限定（DNS 規則準拠）
 */
export const ALLOWED_ORIGIN_PATTERNS: ReadonlyArray<RegExp> = [
  /^https:\/\/[a-z0-9-]+\.studioiframesandbox\.com$/,
]

export function isAllowedOrigin(origin: string): boolean {
  if (ALLOWED_ORIGINS.includes(origin)) return true
  return ALLOWED_ORIGIN_PATTERNS.some((pattern) => pattern.test(origin))
}

export function isCompatibleVersion(version: string): boolean {
  return version === PROTOCOL_VERSION
}

/**
 * 受信したデータが MoovyMessage の形式に従っているか判定する type guard
 *
 * 不正な type / version / instanceId は弾く。payload の中身は呼び出し側で個別に検証する。
 */
export function isMoovyMessage(data: unknown): data is MoovyMessage {
  if (data == null || typeof data !== 'object') return false
  const obj = data as Record<string, unknown>
  if (typeof obj.type !== 'string') return false
  if (typeof obj.version !== 'string') return false
  if (typeof obj.instanceId !== 'string') return false

  const validTypes: ReadonlyArray<string> = Object.values(MESSAGE_TYPES)
  if (!validTypes.includes(obj.type)) return false

  if (obj.type === MESSAGE_TYPES.OPEN) {
    if (obj.payload == null || typeof obj.payload !== 'object') return false
    const payload = obj.payload as Record<string, unknown>
    if (typeof payload.scenarioId !== 'string') return false
    if (payload.tenantId !== undefined && typeof payload.tenantId !== 'string') return false
    if (payload.videoId !== undefined && typeof payload.videoId !== 'string') return false
    if (!isValidSharedDataShape(payload)) return false
  }

  if (obj.type === MESSAGE_TYPES.PREPARE) {
    if (obj.payload == null || typeof obj.payload !== 'object') return false
    const payload = obj.payload as Record<string, unknown>
    if (typeof payload.scenarioId !== 'string') return false
    if (payload.tenantId !== undefined && typeof payload.tenantId !== 'string') return false
    if (!isValidSharedDataShape(payload)) return false
  }

  return true
}

/**
 * scenario / tenant の同梱データの shape を検証する。
 * OPEN（案C）と PREPARE（案E）で共通利用する。
 *
 * 厳密な内部スキーマ検証は行わず「object か null か未指定」のみで弾く。
 * version 一致チェック（isCompatibleVersion）が形式互換を担保する。
 */
function isValidSharedDataShape(payload: Record<string, unknown>): boolean {
  if (
    payload.scenario !== undefined &&
    (payload.scenario === null || typeof payload.scenario !== 'object')
  ) {
    return false
  }
  if (
    payload.tenant !== undefined &&
    payload.tenant !== null &&
    typeof payload.tenant !== 'object'
  ) {
    return false
  }
  return true
}

/**
 * 一意な instanceId を生成
 *
 * 同一ページに複数の Embed Box が並ぶケースで、各子インスタンスを識別するために使う。
 * crypto.randomUUID が無い環境ではタイムスタンプ＋乱数のフォールバックを返す。
 */
export function generateInstanceId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `moovy-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

export type { MessageType }
